import { memo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiIcon } from "@miplatform/components";
import { mdiGiftOutline } from "@mdi/js";
import { Box, Tooltip } from "@mui/material";

/**
 * OrderHolidayIndicator Component
 *
 * Renders a birthday-cake icon with a tooltip showing the holiday name
 * when the order's approved start date falls on a tenant holiday.
 *
 * Follows the same pattern as OrderAlerts — small, memoized, tooltip-wrapped indicator.
 *
 * @component
 * @param {Object} props
 * @param {Object|null} props.holiday - The matching holiday object ({ name, startDate, endDate }). Renders nothing when null.
 * @returns {JSX.Element|null}
 */
export const OrderHolidayIndicator = memo(({ holiday }) => {
  const { classes, theme } = useStyles();

  if (!holiday) return null;

  return (
    <Tooltip
      title={`Requested Date Falls on Holiday: ${holiday.name}`}
      slotProps={{
        popper: { placement: "top", sx: { p: 0 } },
        tooltip: {
          sx: {
            backgroundColor: theme.palette.warning.dark,
            fontSize: theme.fontSize.medium,
          },
        },
      }}
    >
      <Box className={classes.holidayIconContainer}>
        <MiIcon
          path={mdiGiftOutline}
          size={0.6}
          color={theme.palette.common.white}
        />
      </Box>
    </Tooltip>
  );
});

const useStyles = makeStyles()((theme) => ({
  holidayIconContainer: {
    backgroundColor: theme.palette.orange.main,
    padding: theme.spacing(0.5, 1),
    margin: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 4,
    border: `3px solid ${theme.palette.orange.dark}`,
  },
}));
